<template>
  <div
    class="trip-card"
    :class="{ 'menu-open': isMenuOpen, 'menu-open-up': openMenuUp }"
    @click="handleCardClick"
  >
    <!-- 上部: 画像/プレースホルダー + バッジ + ケバブメニュー -->
    <div class="card-media-wrapper">
      <div class="card-image-box">
        <img
          v-if="hasImage && imageUrl && !imageError"
          :src="imageUrl"
          alt=""
          class="trip-image"
          loading="lazy"
          decoding="async"
          @error="handleImageError"
        />
        <div v-else class="image-placeholder-green">
          <IconSvg name="plane-solid-full" size="xl" class="plane-icon" />
        </div>
      </div>

      <!-- 左上: メンバー/作成者ピルバッジ -->
      <div class="card-badge-pill" :title="memberDisplay">
        <span>{{ memberDisplay }}</span>
      </div>

      <!-- 閲覧のみバッジ（必要な場合） -->
      <div v-if="isReadOnly" class="card-readonly-pill">
        <span>閲覧のみ</span>
      </div>

      <!-- 右上: ケバブメニューボタン -->
      <div class="kebab-menu-container" @click.stop>
        <button
          class="kebab-button"
          @click="toggleMenu"
          :aria-label="'しおりメニュー'"
        >
          <span class="kebab-dot"></span>
          <span class="kebab-dot"></span>
          <span class="kebab-dot"></span>
        </button>

        <!-- ドロップダウンメニュー -->
        <transition name="menu-fade">
          <div
            v-if="isMenuOpen"
            class="dropdown-menu"
            :class="{ 'open-up': openMenuUp }"
          >
            <button v-if="canManage" class="menu-item" @click="handleManage">
              <span class="menu-icon">
                <IconSvg name="gear-solid-full" size="sm" />
              </span>
              <span>しおりの管理</span>
            </button>
            <button v-if="canManage" class="menu-item" @click="handleShare">
              <span class="menu-icon">
                <IconSvg name="user-solid-full" size="sm" />
              </span>
              <span>共有</span>
            </button>
            <button class="menu-item" @click="handleCopy">
              <span class="menu-icon">
                <IconSvg name="copy-solid-full" size="sm" />
              </span>
              <span>コピーを作成</span>
            </button>
            <button
              v-if="isOwner"
              class="menu-item menu-item-danger"
              @click="handleDelete"
            >
              <span class="menu-icon">
                <IconSvg name="trash-solid-full" size="sm" />
              </span>
              <span>削除</span>
            </button>
          </div>
        </transition>
      </div>
    </div>

    <!-- 下部: タイトル・日程・費用・レビュー得点 -->
    <div class="card-body">
      <h2 class="trip-card-title">{{ title }}</h2>

      <div class="card-meta-list">
        <div class="meta-row date-row" v-if="startDate || endDate">
          <IconSvg name="calendar-solid-full" size="xs" class="meta-icon" />
          <span class="meta-text">{{ startDate }}<template v-if="endDate"> 〜 {{ endDate }}</template></span>
        </div>

        <div v-if="totalCost > 0" class="meta-row cost-row">
          <span class="cost-currency">¥</span>
          <span class="cost-value">{{ formatCost(totalCost) }}</span>
        </div>

        <div v-if="reviewScore !== null" class="card-review-score">
          <span class="review-score-label">総合得点</span>
          <strong>{{ formatReviewScore(reviewScore) }}/5</strong>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import IconSvg from "@/components/IconSvg.vue";
import {
  fetchAuthenticatedShioriImageUrl,
  resolveEmbeddedShioriImageUrl,
  revokeShioriImageUrl,
} from "@/utils/shiori-image.js";

export default {
  name: "TripCard",
  components: {
    IconSvg,
  },
  props: {
    title: {
      type: String,
      default: "無題の旅行",
    },
    members: {
      type: Array,
      default: () => [],
    },
    people: {
      type: Number,
      default: 0,
    },
    startDate: {
      type: String,
      default: "",
    },
    endDate: {
      type: String,
      default: "",
    },
    shioriId: {
      type: String,
      default: "",
    },
    hasImage: {
      type: Boolean,
      default: false,
    },
    imageSrc: {
      type: String,
      default: "",
    },
    isGuest: {
      type: Boolean,
      default: false,
    },
    totalCost: {
      type: Number,
      default: 0,
    },
    reviewScore: {
      type: Number,
      default: null,
    },
    permissionLevel: {
      type: String,
      default: "READ",
    },
  },
  emits: ["manage", "copy", "delete", "click", "share"],
  setup(props, { emit }) {
    const isMenuOpen = ref(false);
    const openMenuUp = ref(false);
    const imageError = ref(false);
    const imageUrl = ref("");
    let imageRequestVersion = 0;
    const menuInstanceId = `trip-card-menu-${Math.random()
      .toString(36)
      .slice(2)}`;

    // 費用をフォーマットする関数
    const formatCost = (cost) => {
      return new Intl.NumberFormat("ja-JP").format(cost) + " 円";
    };

    const formatReviewScore = (score) => {
      const numberScore = Number(score);
      return Number.isFinite(numberScore) ? numberScore.toFixed(1) : "-";
    };

    const memberDisplay = computed(() => {
      if (props.members && props.members.length > 0) {
        const displayNames = props.members.slice(0, 2).join(", ");
        const remaining = props.members.length - 2;
        return remaining > 0
          ? `${displayNames} 他${remaining}名`
          : displayNames;
      } else if (props.people > 0) {
        return `${props.people}名`;
      }
      return "メンバー未設定";
    });

    const normalizedPermission = computed(() => {
      const permission = String(props.permissionLevel || "")
        .trim()
        .toUpperCase();
      if (permission === "OWNER" || permission === "ADMIN") return permission;
      if (permission === "WRITE" || permission === "EDIT") return "WRITE";
      return "READ";
    });

    const canManage = computed(() =>
      ["OWNER", "ADMIN"].includes(normalizedPermission.value),
    );
    const isOwner = computed(() => normalizedPermission.value === "OWNER");
    const isReadOnly = computed(() => normalizedPermission.value === "READ");

    const releaseImageUrl = () => {
      revokeShioriImageUrl(imageUrl.value);
      imageUrl.value = "";
    };

    watch(
      () => [props.hasImage, props.imageSrc, props.shioriId, props.isGuest],
      async () => {
        const requestVersion = ++imageRequestVersion;
        releaseImageUrl();
        imageError.value = false;
        if (!props.hasImage) return;

        const embeddedImageUrl = resolveEmbeddedShioriImageUrl({
          HAS_IMAGE: props.hasImage,
          IMAGE_DATA_URI: props.imageSrc,
          IMAGE_URL: props.imageSrc,
          __guest: props.isGuest,
        });
        if (embeddedImageUrl) {
          imageUrl.value = embeddedImageUrl;
          return;
        }

        try {
          const nextImageUrl = await fetchAuthenticatedShioriImageUrl(
            props.shioriId,
          );
          if (requestVersion !== imageRequestVersion) {
            revokeShioriImageUrl(nextImageUrl);
            return;
          }
          imageUrl.value = nextImageUrl;
          imageError.value = !nextImageUrl;
        } catch {
          if (requestVersion === imageRequestVersion) imageError.value = true;
        }
      },
      { immediate: true },
    );

    const handleImageError = () => {
      releaseImageUrl();
      imageError.value = true;
    };

    const shouldOpenUpward = (buttonElement) => {
      if (!buttonElement || typeof window === "undefined") {
        return false;
      }

      const rect = buttonElement.getBoundingClientRect();
      const menuHeight = 180;
      const bottomFloatingButtonArea = window.innerWidth <= 640 ? 96 : 24;
      return (
        window.innerHeight - rect.bottom <
        menuHeight + bottomFloatingButtonArea
      );
    };

    const toggleMenu = (event) => {
      if (isMenuOpen.value) {
        closeMenu();
        return;
      }

      openMenuUp.value = shouldOpenUpward(event.currentTarget);
      document.dispatchEvent(
        new CustomEvent("rabiitrip:trip-card-menu-open", {
          detail: menuInstanceId,
        }),
      );
      isMenuOpen.value = true;
    };

    const closeMenu = () => {
      isMenuOpen.value = false;
      openMenuUp.value = false;
    };

    const handleCardClick = () => {
      if (!isMenuOpen.value) {
        emit("click");
      }
    };

    const handleManage = () => {
      if (!canManage.value) return;
      emit("manage", props.shioriId);
      closeMenu();
    };

    const handleShare = () => {
      if (!canManage.value) return;
      emit("share", props.shioriId);
      closeMenu();
    };

    const handleCopy = () => {
      emit("copy", props.shioriId);
      closeMenu();
    };

    const handleDelete = () => {
      if (!isOwner.value) return;
      emit("delete", props.shioriId);
      closeMenu();
    };

    const handleClickOutside = () => {
      if (isMenuOpen.value) {
        closeMenu();
      }
    };

    const handleOtherMenuOpen = (event) => {
      if (event.detail !== menuInstanceId) {
        closeMenu();
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener(
        "rabiitrip:trip-card-menu-open",
        handleOtherMenuOpen,
      );
    });

    onUnmounted(() => {
      imageRequestVersion += 1;
      releaseImageUrl();
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener(
        "rabiitrip:trip-card-menu-open",
        handleOtherMenuOpen,
      );
    });

    return {
      isMenuOpen,
      openMenuUp,
      imageError,
      imageUrl,
      memberDisplay,
      canManage,
      isOwner,
      isReadOnly,
      formatCost,
      formatReviewScore,
      toggleMenu,
      handleCardClick,
      handleManage,
      handleShare,
      handleCopy,
      handleDelete,
      handleImageError,
    };
  },
};
</script>

<style scoped>
.trip-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 1.25rem; /* 大きめの角丸 */
  box-shadow: 0 4px 16px rgba(18, 33, 52, 0.04);
  position: relative;
  z-index: 0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
}

.trip-card.menu-open {
  z-index: 1000;
}

.trip-card:hover {
  box-shadow: 0 8px 24px rgba(18, 33, 52, 0.08);
  transform: translateY(-3px);
}

/* ── 上部メディアエリア ── */
.card-media-wrapper {
  position: relative;
  width: 100%;
  height: 160px;
  background: #b4cf9e;
  overflow: hidden;
}

.card-image-box {
  width: 100%;
  height: 100%;
}

.trip-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.image-placeholder-green {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #b4cf9e 0%, #c4ddb0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.plane-icon {
  color: rgba(94, 122, 77, 0.45);
  font-size: 2.5rem;
}

/* ── 左上バッジピル ── */
.card-badge-pill {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: rgba(18, 33, 52, 0.72);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.625rem;
  border-radius: var(--radius-full);
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: 0.02em;
}

.card-readonly-pill {
  position: absolute;
  top: 0.75rem;
  left: auto;
  right: 3.5rem;
  background: rgba(100, 116, 139, 0.8);
  backdrop-filter: blur(8px);
  color: #ffffff;
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: var(--radius-full);
}

/* ── 右上ケバブボタン ── */
.kebab-menu-container {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 10;
}

.kebab-button {
  width: 2rem;
  height: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  background: rgba(18, 33, 52, 0.72);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
  padding: 0;
}

.kebab-button:hover {
  background: rgba(18, 33, 52, 0.9);
  transform: scale(1.05);
}

.kebab-dot {
  width: 0.22rem;
  height: 0.22rem;
  background: #ffffff;
  border-radius: 50%;
}

/* ── 下部コンテンツエリア ── */
.card-body {
  padding: 1.125rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  background: var(--color-surface);
}

.trip-card-title {
  font-size: 1.0625rem;
  font-weight: 800;
  color: var(--color-text);
  margin: 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-meta-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8125rem;
  color: var(--color-text-muted);
}

.date-row .meta-icon {
  color: var(--color-text-muted);
  flex-shrink: 0;
}

.meta-text {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  line-height: 1.3;
}

.cost-row {
  margin-top: 0.25rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #5e7a4d;
}

.cost-currency {
  font-weight: 800;
}

.cost-value {
  letter-spacing: -0.01em;
}

.card-review-score {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  width: fit-content;
  margin-top: 0.25rem;
  padding: 0.2rem 0.5rem;
  color: var(--color-navy);
  background: var(--color-cool-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  font-size: 0.6875rem;
  font-weight: 700;
}

.review-score-label {
  color: var(--color-text-muted);
}

.card-review-score strong {
  color: var(--color-navy);
  font-size: 0.75rem;
}

/* ── ドロップダウンメニュー ── */
.dropdown-menu {
  position: absolute;
  top: 2.5rem;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-xl);
  min-width: 11rem;
  overflow: hidden;
  z-index: 1002;
}

.dropdown-menu.open-up {
  top: auto;
  bottom: 2.5rem;
}

.menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  background: var(--color-surface);
  border: none;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
  transition: background-color var(--transition-fast);
}

.menu-item:hover {
  background: var(--color-background);
}

.menu-item:not(:last-child) {
  border-bottom: 1px solid var(--color-border-light);
}

.menu-item-danger {
  color: var(--color-danger);
}

.menu-item-danger:hover {
  background: var(--color-danger-light);
}

.menu-icon {
  font-size: 0.875rem;
}

/* ── トランジション ── */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.menu-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.menu-open-up .menu-fade-enter-from,
.menu-open-up .menu-fade-leave-to {
  transform: translateY(8px) scale(0.95);
}

@media (max-width: 767px) {
  .trip-card {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0.75rem 0.875rem;
    gap: 0.875rem;
    min-height: auto;
    border-radius: var(--radius-lg);
  }

  .card-media-wrapper {
    width: 60px;
    height: 60px;
    border-radius: var(--radius-md);
    flex-shrink: 0;
    margin: 0;
  }

  .card-badge-pill,
  .card-readonly-pill {
    display: none; /* スマホのリストカードではバッジをbody内に集約 */
  }

  .kebab-menu-container {
    position: relative;
  }

  .kebab-button {
    background: none;
    border: none;
    color: var(--color-text-muted);
    box-shadow: none;
    width: 32px;
    height: 32px;
  }

  .kebab-dot {
    background: var(--color-text-muted);
  }

  .card-body {
    padding: 0;
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .trip-card-title {
    font-size: 0.9375rem;
    font-weight: 800;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
  }

  .card-meta-list {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    color: var(--color-text-muted);
    flex-wrap: wrap;
    margin: 0;
  }

  .meta-row {
    font-size: 0.75rem;
  }

  .card-footer {
    display: none;
  }
}
</style>
